import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { LanguageSelector } from "@/components/language-selector";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useInitialLanguage } from "@/hooks/use-initial-language";
import { useResetPasswordForm } from "./use-reset-password-form";
import { useTokenValidation } from "./use-token-validation";

export function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tForms } = useTranslation("forms");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Handle initial language from URL query parameter
  useInitialLanguage();

  // Validate token on mount
  const { isValidating, isValid, validationMessage } = useTokenValidation(token);

  const { form, isLoading, onSubmit } = useResetPasswordForm({
    token: token || "",
    onSuccess: () => setResetSuccess(true),
  });

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        {/* Language Selector - Desktop (top-right) */}
        <div className="absolute top-4 right-4 hidden sm:block">
          <LanguageSelector />
        </div>
        <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
                  <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <CardTitle className="text-2xl text-center">{t("resetPassword.success")}</CardTitle>
              <CardDescription className="text-center">
                {t("resetPassword.successMessage")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login">
                <Button className="w-full">{t("resetPassword.goToLogin")}</Button>
              </Link>
            </CardContent>
          </Card>

          {/* Language Selector - Mobile (under card) */}
          <div className="flex justify-center sm:hidden">
            <LanguageSelector />
          </div>
        </div>
      </div>
    );
  }

  // Loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        {/* Language Selector - Desktop (top-right) */}
        <div className="absolute top-4 right-4 hidden sm:block">
          <LanguageSelector />
        </div>
        <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
          <Card className="border-border/50 shadow-lg">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-4 text-sm text-muted-foreground">Validating reset link...</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!token || !isValid) {
    let errorTitle = t("resetPassword.invalidToken");
    if (validationMessage?.includes("already been used")) {
      errorTitle = t("resetPassword.tokenUsed");
    } else if (validationMessage?.includes("expired")) {
      errorTitle = t("resetPassword.tokenExpired");
    }

    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        {/* Language Selector - Desktop (top-right) */}
        <div className="absolute top-4 right-4 hidden sm:block">
          <LanguageSelector />
        </div>
        <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
          <Card className="border-border/50 shadow-lg">
            <CardHeader className="space-y-1">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-3">
                  <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <CardTitle className="text-2xl text-center">{errorTitle}</CardTitle>
              {validationMessage && (
                <CardDescription className="text-center">{validationMessage}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <Link to="/forgot-password">
                <Button variant="outline" className="w-full">
                  {t("resetPassword.requestNewLink")}
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" className="w-full">
                  {t("resetPassword.goToLogin")}
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Language Selector - Mobile (under card) */}
          <div className="flex justify-center sm:hidden">
            <LanguageSelector />
          </div>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      {/* Language Selector - Desktop (top-right) */}
      <div className="absolute top-4 right-4 hidden sm:block">
        <LanguageSelector />
      </div>
      <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[400px]">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">96Beauty</h1>
          <p className="text-muted-foreground">{t("resetPassword.subtitle")}</p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center">{t("resetPassword.title")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <Form {...form}>
              <form onSubmit={onSubmit} className="space-y-4">
                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("resetPassword.newPassword")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder={tForms("placeholders.password")}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("resetPassword.confirmPassword")}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder={tForms("placeholders.password")}
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {showConfirmPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full" disabled={isLoading} size="lg">
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isLoading ? t("resetPassword.resetting") : t("resetPassword.resetPassword")}
                </Button>
              </form>
            </Form>

            <div className="text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                {t("forgotPassword.backToLogin")}
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Language Selector - Mobile (under card) */}
        <div className="flex justify-center sm:hidden">
          <LanguageSelector />
        </div>
      </div>
    </div>
  );
}
